<template>
    <b-modal id="bv-modal-email-group" size="lg" :title="isEditing ? 'Editar grupo' : 'Adicionar novo grupo'" scrollable centered no-stacking>
        <b-container fluid>
            <form id="email-group-form">
                <div class="row">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="group-name">Nome <span class="text-danger">*</span></label>
                            <input
                                id="group-name"
                                v-model="form.name"
                                type="text"
                                class="form-control"
                                placeholder="Nome do grupo"
                            />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label>Contatos</label>
                            <div class="multiselect-container">
                                <input
                                    type="text"
                                    v-model="searchTerm"
                                    placeholder="Pesquisar contatos..."
                                    class="form-control mb-2"
                                >
                                <div class="checkbox-list">
                                    <div v-if="filteredContacts.length === 0" class="text-muted text-center py-2">
                                        Nenhum contato encontrado.
                                    </div>
                                    <div v-for="contact in filteredContacts" :key="contact.id" class="form-check">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            :id="`contact-${contact.id}`"
                                            :value="contact.id"
                                            v-model="form.members"
                                        >
                                        <label class="form-check-label" :for="`contact-${contact.id}`">
                                            {{ contact.name }}
                                            <small class="text-muted ml-1">{{ contact.email }}</small>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </b-container>

        <template #modal-footer>
            <div class="modal-footer-wrapper">
                <button type="submit" @click.prevent="submitForm" class="btn btn-primary" :disabled="loading">
                    <span v-if="loading">
                        <i class="fas fa-spinner fa-pulse"></i> Aguarde...
                    </span>
                    <span v-else>
                        <i class="fas fa-save"></i> Salvar
                    </span>
                </button>

                <button v-if="isEditing" type="button" @click.prevent="deleteGroup" class="btn btn-danger" :disabled="loading">
                    <i class="fas fa-trash-alt"></i> Deletar
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: "EmailGroupModal",
    data() {
        return {
            loading: false,
            allContacts: [],
            dataEdit: {},
            searchTerm: '',
            form: {
                name: '',
                members: [],
            }
        }
    },
    computed: {
        isEditing() {
            return this.dataEdit && this.dataEdit.id;
        },
        filteredContacts() {
            if (!this.searchTerm) return this.allContacts;
            const term = this.searchTerm.toLowerCase();
            return this.allContacts.filter(c =>
                c.name.toLowerCase().includes(term) ||
                c.email.toLowerCase().includes(term)
            );
        }
    },
    watch: {
        dataEdit: {
            handler(newData) {
                if (newData && Object.keys(newData).length > 0) {
                    this.loadFormData(newData);
                }
            },
            immediate: true
        }
    },
    methods: {
        async fetchContacts() {
            axios.get("/settings/contacts/type/email").then(response => {
                this.allContacts = response.data.map(c => ({
                    id:    c.id,
                    name:  c.name,
                    email: c.contact,
                }));
            }).catch(error => {
                console.error(error);
                Swal.fire({ type: 'error', text: 'Falha ao carregar contatos!' });
            });
        },
        async openModal(data = null) {
            this.resetForm();
            await this.fetchContacts();
            if (data) {
                this.dataEdit = data;
                this.loadFormData(data);
            }
            this.$bvModal.show('bv-modal-email-group');
        },
        closeModal(data = null) {
            this.loading = false;
            this.resetForm();
            if (data) this.$emit("updateGroupTable", data);
            this.$bvModal.hide('bv-modal-email-group');
        },
        loadFormData(data) {
            this.form.id      = data.id;
            this.form.name    = data.name;
            this.form.members = data.members ? data.members.map(m => m.id) : [];
        },
        resetForm() {
            this.form = {
                name: '',
                members: [],
            };
            this.searchTerm = '';
            this.dataEdit   = {};
        },
        validateForm() {
            if (!this.form.name || !this.form.name.trim()) {
                Swal.fire({ type: 'error', text: "Campo 'Nome' é obrigatório!" });
                return false;
            }
            return true;
        },
        async deleteGroup() {
            const { value } = await Swal.fire({
                title: 'Deseja deletar este grupo?',
                text: 'Esta ação não poderá ser desfeita.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, deletar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#dc3545',
                reverseButtons: true,
                showCloseButton: true,
            });

            if (!value) return;

            this.loading = true;

            try {
                await axios.delete(`/settings/contacts/groups/${this.form.id}`);
                Swal.fire({ type: 'success', text: 'Grupo deletado com sucesso.' });
                this.$emit("deleteGroup", this.form.id);
                this.closeModal();
            } catch (error) {
                console.error(error);
                const message = error.response?.data?.error || 'Falha ao deletar grupo!';
                Swal.fire({ type: 'error', text: message });
                this.loading = false;
            }
        },
        async submitForm() {
            if (!this.validateForm()) return;
            this.loading = true;

            const formData = {
                name:     this.form.name,
                type:     'email',
                contacts: this.form.members,
            };
        
            try {
                let response;
            
                if (this.form.id) {
                    response = await axios.put(`/settings/contacts/groups/${this.form.id}`, formData);
                    Swal.fire({ type: 'success', text: 'Grupo atualizado com sucesso.' });
                } else {
                    response = await axios.post("/settings/contacts/groups", formData);
                    Swal.fire({ type: 'success', text: 'Grupo salvo com sucesso.' });
                }
            
                const data = {
                    ...response.data,
                    members: response.data.contacts,
                };
            
                this.closeModal(data);
            } catch (error) {
                console.error(error);
                const message = error.response?.data?.error
                    ? Object.values(error.response.data.error).flat().join('\n')
                    : 'Falha ao salvar grupo! Valide todos os campos e tente novamente.';
                Swal.fire({ type: 'error', text: message });
                this.loading = false;
            }
        },
    },
}
</script>

<style scoped>
.checkbox-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 10px;
}

.modal-footer-wrapper {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    width: 100%;
}
</style>